import { ControlledDateField, ControlledTextField } from 'components/Form';
import { ModalDialog } from 'components/ModalDialog';
import { useRequest } from 'features/request/useRequest';
import { ActKc2StatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { ConstructionTypeIcon } from 'pages/Project/Common/types';
import type { IconComponent } from 'uikit';
import { Grid, GridItem, IconCalendar } from 'uikit';
import { disableKeyButNumber } from 'utils/disableKeyButNumber';

import type { ForwardedRef } from 'react';
import type React from 'react';
import { useState, useCallback, useEffect, useMemo, forwardRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { useValidationActKC2 } from '../hooks/useValidationActKC2';
import { gqlIsUniqueActKC2InternalNumber } from '../workOrderQueries';

import { ActKC2CostGroupField } from './ActKC2CostGroupField';
import { ActKC2InflationSeason } from './ActKC2InflationSeason';
import { ActKC2SeasonField } from './ActKC2SeasonField';
import { ActKC2StatusField } from './ActKC2StatusField';
import type { IActKC2Data } from './EditActKC2Modal';

interface ICreateMtrFormProps {
    workOrderObjectId: number;
    onSubmit: (values: any) => void;
    statusWorkOrder?: WorkOrderStatusEnum;
    defaultValues?: IActKC2Data | undefined | null;
    constructionTypeCode?: string;
    setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
    actKC2Id?: number;
    isCreating?: boolean;
    aktKC2Rules?: IUseAccess['aktKC2'];
    isCopied?: boolean | null;
    isSKzSAct?: boolean | null;
    isCorrection?: boolean | null;
    isInactive?: boolean | null;
}

export enum ActKC2Fields {
    authoredBy = 'authoredBy',
    chapter = 'chapter',
    contractor = 'contractor',
    contractorAddress = 'contractorAddress',
    costGroup = 'costGroup',
    createdAt = 'createdAt',
    date = 'date',
    number = 'number',
    object = 'object',
    reportingFinishDate = 'reportingFinishDate',
    reportingStartDate = 'reportingStartDate',
    seasonId = 'seasonId',
    status = 'status',
    inflationSeasonId = 'inflationSeasonId',
    fullNumber = 'fullNumber',
    correctionDate = 'correctionDate',
}

export const ActKC2EditForm = forwardRef(
    (
        {
            defaultValues,
            onSubmit,
            constructionTypeCode,
            actKC2Id,
            setIsDirty,
            isCreating = false,
            workOrderObjectId,
            aktKC2Rules,
            isCopied,
            statusWorkOrder,
            isCorrection,
            isInactive,
            isSKzSAct,
        }: ICreateMtrFormProps,
        formRef: ForwardedRef<HTMLFormElement>,
    ) => {
        const [isOpenDialogStatus, setIsOpenDialogStatus] = useState<boolean>(false);

        const {
            control,
            handleSubmit,
            setError,
            clearErrors,
            formState: { isDirty, dirtyFields },
            resetField,
            watch,
        } = useForm({
            criteriaMode: 'all',
            defaultValues: defaultValues as any,
        });

        const seasonIdValue = watch('seasonId');
        const inflationSeasonValue = watch('inflationSeasonId');

        useEffect(() => {
            if (inflationSeasonValue?.id > seasonIdValue?.id) {
                resetField('inflationSeasonId');
            }
        }, [inflationSeasonValue?.id, resetField, seasonIdValue]);

        const statusAktKC2 = useWatch({
            control,
            name: 'status',
        });

        const numberField = useWatch({
            control,
            name: 'number',
        });

        const isDisabledByWorkOrderStatus = useMemo(() => {
            const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;
            const isStatusWorkOrderCompleted = statusWorkOrder === WorkOrderStatusEnum.Completed;

            return isStatusWorkOrderDelayed || isStatusWorkOrderCompleted;
        }, [statusWorkOrder]);

        const isDisabledByStatus = useMemo(
            () =>
                isDisabledByWorkOrderStatus ||
                ![ActKc2StatusEnum.InProgress, ActKc2StatusEnum.OnCheck].includes(statusAktKC2.id),
            [isDisabledByWorkOrderStatus, statusAktKC2.id],
        );

        const isRequiredField = useMemo(() => statusAktKC2.id !== ActKc2StatusEnum.InProgress, [statusAktKC2]);

        const { validateByStatus, validateDateRange } = useValidationActKC2(isRequiredField);

        useEffect(() => {
            if (statusAktKC2.id === ActKc2StatusEnum.InProgress) {
                clearErrors(['number', 'date', 'reportingDate']);
            }
        }, [clearErrors, statusAktKC2.id]);

        useEffect(() => {
            if (setIsDirty) {
                setIsDirty(isDirty);
            }

            return () => {
                if (setIsDirty) {
                    setIsDirty(false);
                }
            };
        }, [isDirty, setIsDirty]);

        const parseValueNumber = useCallback((event) => disableKeyButNumber(event), []);

        const variables = useMemo(
            () => ({
                actKC2Id: Number.isFinite(actKC2Id) ? actKC2Id : null,
                actKC2Number: Number(numberField),
                workOrderObjectId,
            }),
            [actKC2Id, numberField, workOrderObjectId],
        );

        const [data] = useRequest({
            requestId: 'IsUniqueActKC2InternalNumber',
            query: gqlIsUniqueActKC2InternalNumber,
            variables,
            withEffect: !!numberField,
        });

        const isUniqInternalNumber = data?.uerIsUniqueActKC2InternalNumber?.result;

        useEffect(() => {
            if (numberField && defaultValues?.number !== numberField && !isUniqInternalNumber) {
                setError('number', { message: 'Акт КС2 с таким номером уже существует' });
            } else {
                clearErrors('number');
            }
        }, [clearErrors, numberField, defaultValues?.number, isUniqInternalNumber, setError]);

        useEffect(() => {
            if (dirtyFields.status) {
                setIsOpenDialogStatus(true);
            }
        }, [dirtyFields.status, statusAktKC2.id]);

        const handleCancelDialogStatus = useCallback(() => {
            resetField('status');
            setIsOpenDialogStatus(false);
        }, [resetField]);

        const handleSubmitDialogStatus = useCallback(() => {
            setIsOpenDialogStatus(false);
        }, []);

        const isActFieldDisabled = useMemo(
            () => isDisabledByStatus || !!isCorrection,
            [isDisabledByStatus, isCorrection],
        );
        const currentStatusCompleted = useMemo(
            () => defaultValues?.status?.id && [ActKc2StatusEnum.Accepted].includes(defaultValues?.status?.id),
            [defaultValues?.status?.id],
        );

        const isStatusDisabled = useMemo(
            () =>
                (!aktKC2Rules?.canEditCompletedStatus && currentStatusCompleted) ||
                isDisabledByWorkOrderStatus ||
                !!isInactive,
            [aktKC2Rules?.canEditCompletedStatus, currentStatusCompleted, isDisabledByWorkOrderStatus, isInactive],
        );

        return (
            <>
                <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                    <Grid rowGap="s" cols="1">
                        <GridItem>
                            <ActKC2StatusField
                                name={ActKC2Fields.status}
                                control={control}
                                defaultValue={defaultValues?.status}
                                isCreating={isCreating}
                                disabled={isStatusDisabled}
                                statusFollowers={defaultValues?.statusFollowers}
                            />
                        </GridItem>
                        <GridItem>
                            <ActKC2SeasonField
                                name={ActKC2Fields.seasonId}
                                control={control}
                                disabled={!!isCorrection || (Number.isFinite(actKC2Id) && !isCopied) || !!isSKzSAct}
                            />
                        </GridItem>
                        <GridItem>
                            <ActKC2InflationSeason
                                name={ActKC2Fields.inflationSeasonId}
                                control={control}
                                disabled={isDisabledByStatus}
                                seasonIdValue={seasonIdValue?.id}
                            />
                        </GridItem>
                        <GridItem>
                            <ActKC2CostGroupField name={ActKC2Fields.costGroup} control={control} readOnly />
                        </GridItem>

                        <GridItem>
                            <ControlledTextField
                                name="objectName"
                                label="Объект"
                                control={control}
                                type="text"
                                view="clear"
                                readOnly
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name="constructionType"
                                label="Вид строительства"
                                control={control}
                                type="text"
                                view="clear"
                                readOnly
                                leftSide={
                                    constructionTypeCode &&
                                    (ConstructionTypeIcon[constructionTypeCode] as IconComponent)
                                }
                                iconSize="m"
                            />
                        </GridItem>
                        <GridItem>
                            <div className="ActKC2FieldWrapper">
                                <ControlledTextField
                                    name={ActKC2Fields.number}
                                    label={isCorrection ? 'Номер исходного Акта КС-2' : 'Номер'}
                                    control={control}
                                    type="number"
                                    placeholder={isCorrection ? 'Номер исходного Акта КС-2' : 'Введите'}
                                    required={!isActFieldDisabled && isRequiredField}
                                    rules={{ validate: validateByStatus }}
                                    directionTooltip="upCenter"
                                    onKeyDown={parseValueNumber}
                                    disabled={isActFieldDisabled}
                                />
                                <ControlledDateField
                                    name={ActKC2Fields.date}
                                    label="от"
                                    control={control}
                                    required={!isActFieldDisabled && isRequiredField}
                                    rules={{ validate: validateByStatus }}
                                    disabled={isActFieldDisabled}
                                    rightSide={IconCalendar}
                                    withClearButton={!isActFieldDisabled}
                                />
                            </div>
                        </GridItem>
                        {isCorrection && (
                            <GridItem>
                                <div className="ActKC2FieldWrapper">
                                    <ControlledTextField
                                        name={ActKC2Fields.fullNumber}
                                        label="Номер корректировочного Акта КС-2"
                                        control={control}
                                        placeholder="Номер корректировочного Акта КС-2"
                                        required={isRequiredField}
                                        rules={{ validate: validateByStatus }}
                                        directionTooltip="upCenter"
                                        classNameTooltip="ActKC2Number"
                                        disabled
                                    />
                                    <ControlledDateField
                                        name={ActKC2Fields.correctionDate}
                                        label="от"
                                        control={control}
                                        required={isRequiredField}
                                        rules={{ validate: validateByStatus }}
                                        rightSide={IconCalendar}
                                    />
                                </div>
                            </GridItem>
                        )}
                        <GridItem>
                            <ControlledDateField
                                name="reportingDate"
                                type="date-range"
                                label="Отчетный период"
                                control={control}
                                rules={{ validate: validateDateRange }}
                                disabled={isActFieldDisabled || !!isSKzSAct}
                                required={!isActFieldDisabled && isRequiredField}
                                rightSide={[IconCalendar, IconCalendar]}
                                withClearButton={!isActFieldDisabled}
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={ActKC2Fields.contractor}
                                label="Подрядчик"
                                control={control}
                                type="text"
                                placeholder="Подрядчик"
                                disabled
                            />
                        </GridItem>
                        <GridItem>
                            <ControlledTextField
                                name={ActKC2Fields.contractorAddress}
                                label="Адрес Подрядчика"
                                control={control}
                                type="text"
                                placeholder="не указан"
                                disabled
                            />
                        </GridItem>
                    </Grid>
                </form>
                <ModalDialog
                    title="Изменение статуса"
                    isOpen={isOpenDialogStatus}
                    onConfirm={handleSubmitDialogStatus}
                    onClose={handleCancelDialogStatus}
                    btnConfirmLabel="Изменить"
                    btnCancelLabel="Не изменять"
                    withTitleCloseButton
                    bodyMinHeight="100"
                    style={{ width: 520 }}
                >
                    {defaultValues?.status.id && (
                        <div style={{ padding: 20, lineHeight: '24px' }}>
                            <div>
                                {defaultValues.status.id === ActKc2StatusEnum.Accepted && (
                                    <>Вы уверены, что хотите изменить статус на &quot;На проверке&quot;?</>
                                )}
                                {defaultValues.status.id === ActKc2StatusEnum.InProgress && (
                                    <>
                                        Вы уверены, что хотите изменить статус на &quot;На проверке&quot;? После
                                        перевода в статус &quot;На проверке&quot; внесение изменений в рабочей области
                                        Акта в столбцы с плановыми значениями будет недоступно
                                    </>
                                )}
                                {defaultValues.status.id === ActKc2StatusEnum.OnCheck && (
                                    <>
                                        {statusAktKC2.id === ActKc2StatusEnum.Accepted && (
                                            <>
                                                Вы уверены, что хотите изменить статус на &quot;Согласован&quot;? После
                                                перевода в статус &quot;Согласован&quot; дальнейшая работа по Акту КС-2
                                                будет недоступна.
                                            </>
                                        )}
                                        {statusAktKC2.id === ActKc2StatusEnum.InProgress && (
                                            <>Вы уверены, что хотите изменить статус на &quot;В работе&quot;?</>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </ModalDialog>
            </>
        );
    },
);

ActKC2EditForm.displayName = 'ActKC2EditForm';
